import { useState } from "react";
import { CalendarDays, Clock, DollarSign, CheckCircle2, Trophy, Users } from "lucide-react";
import { Reveal, SectionTag, MaskedLine } from "@/components/Reveal";
import { api } from "@/context/AuthContext";
import { toast } from "sonner";

const WEEKS = [
  { id: "Week 1 — Middle School Foundations (Jun 14–18, 2027)", title: "Middle School Foundations", dates: "June 14–18, 2027", ages: "Ages 11–14", desc: "Fundamentals done right: footwork base, form shooting, handle confidence, and love for the grind." },
  { id: "Week 2 — High School & D1 Prep (Jun 21–25, 2027)", title: "High School & D1 Prep", dates: "June 21–25, 2027", ages: "Grades 9–12 + college", desc: "Varsity-ready intensity: shot volume tracking, P&R reads, agility testing, and recruiting film sessions." },
  { id: "Week 3 — Middle School Foundations II (Jul 12–16, 2027)", title: "Middle School Foundations II", dates: "July 12–16, 2027", ages: "Ages 11–14", desc: "Second session of the foundations curriculum with fresh drills and end-of-week live scrimmages." },
  { id: "Week 4 — High School & D1 Prep II (Jul 19–23, 2027)", title: "High School & D1 Prep II", dates: "July 19–23, 2027", ages: "Grades 9–12 + college", desc: "Final push before fall: competitive live reads, college-style workouts, and a coaches' showcase day." },
];

const MINI_CAMPS = [
  { id: "Thanksgiving Break Mini-Camp (Nov 23–25, 2026)", title: "Thanksgiving Break", dates: "Nov 23–25, 2026", desc: "3-day tune-up over Thanksgiving break: handles, shooting volume, and conditioning before winter ball." },
  { id: "Winter Break Mini-Camp (Dec 28–30, 2026)", title: "Winter Break", dates: "Dec 28–30, 2026", desc: "3 days of mid-season sharpening: game-speed reads, shot work, and competitive small-sided games." },
  { id: "Spring Break Mini-Camp (Mar 29–31, 2027)", title: "Spring Break", dates: "Mar 29–31, 2027", desc: "3-day spring reset: agility testing, skill rebuilds, and a head start on off-season development." },
];

const DAILY = [
  { time: "8:30 AM", item: "Dynamic warmup & mobility" },
  { time: "9:00 AM", item: "Ball-handling lab (two-ball & pressure series)" },
  { time: "10:15 AM", item: "Shooting mechanics & tracked volume" },
  { time: "11:30 AM", item: "Lunch + film / IQ session" },
  { time: "12:15 PM", item: "Agility, deceleration & explosion work" },
  { time: "1:00 PM", item: "Live reads, small-sided games & scrimmage" },
  { time: "2:00 PM", item: "Pickup / parent checkout" },
];

const EMPTY = { parent_name: "", email: "", phone: "", athlete_name: "", athlete_grade: "Middle School", week: WEEKS[0].id, notes: "" };

export default function SummerCamp() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/camp/registrations", form);
      setSent(true);
      toast.success("Camp spot reserved — Theo will confirm details by email.");
      setForm(EMPTY);
    } catch {
      toast.error("Could not reserve the spot. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="pt-16">
      <section className="relative overflow-hidden court-grid-bg">
        <div className="absolute inset-0 bg-gradient-to-b from-court-amber/10 to-transparent pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <div className="inline-flex items-center gap-2 border border-court-amber/40 bg-court-amber/10 rounded-full px-4 py-1.5 mb-8">
            <Trophy size={13} className="text-court-amber" />
            <span className="font-mono text-xs uppercase tracking-widest text-court-amber font-semibold">New for 2027 • Alameda, CA</span>
          </div>
          <h1 data-testid="camp-heading" className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.1}>Summer 2027</MaskedLine>
            <MaskedLine delay={0.25}><span className="text-court-amber">Pre-Season Elite Camp</span></MaskedLine>
          </h1>
          <p className="mt-6 text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
            Week-long camp-style training built to get your athlete fall-season ready: handles, shooting volume, agility testing, basketball IQ film sessions — and a direct line to Theo's college coach network for older groups.
          </p>
          <div className="mt-8 flex flex-wrap gap-6">
            <div className="flex items-center gap-2 text-sm text-zinc-300"><DollarSign size={16} className="text-court-amber" /><span><b className="text-court-amber">$325</b> / week</span></div>
            <div className="flex items-center gap-2 text-sm text-zinc-300"><DollarSign size={16} className="text-court-amber" /><span><b className="text-court-amber">$75</b> / day drop-in</span></div>
            <div className="flex items-center gap-2 text-sm text-zinc-300"><Clock size={16} className="text-court-amber" /><span>8:30 AM – 2:00 PM daily</span></div>
            <div className="flex items-center gap-2 text-sm text-zinc-300"><Users size={16} className="text-court-amber" /><span>Boys & girls, ages 11 through D1</span></div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <Reveal>
          <SectionTag index="SEC.01" label="Camp Weeks" />
          <h2 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-2xl sm:text-3xl lg:text-4xl">Pick your <span className="text-court-amber">week.</span></h2>
        </Reveal>
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {WEEKS.map((w, i) => (
            <Reveal key={w.id} delay={i * 0.06}>
              <div data-testid={`camp-week-${i}`} className="card-lift bg-court-surface/80 border border-white/10 rounded-2xl p-7 h-full">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono text-xs uppercase tracking-widest text-court-amber flex items-center gap-2"><CalendarDays size={13} />{w.dates}</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{w.ages}</span>
                </div>
                <h3 className="font-display font-bold uppercase tracking-tight text-xl sm:text-2xl mt-3">{w.title}</h3>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{w.desc}</p>
                <button data-testid={`camp-week-${i}-select`} onClick={() => { setForm((f) => ({ ...f, week: w.id })); document.getElementById("camp-form")?.scrollIntoView({ behavior: "smooth" }); }}
                  className="mt-5 font-mono text-xs uppercase tracking-widest text-court-amber hover:text-court-amberhover transition-colors">
                  Reserve this week →
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-20">
          <Reveal>
            <SectionTag index="SEC.01B" label="Shorter Breaks" />
            <h2 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-2xl sm:text-3xl lg:text-4xl">
              3-day <span className="text-court-amber">mini-camps.</span>
            </h2>
            <p className="mt-3 text-sm sm:text-base text-zinc-400 max-w-xl">Same Lab intensity packed into shorter school breaks. <span className="text-court-amber font-bold">$175</span> per 3-day camp — reserve now, pay in person on day one.</p>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-3 gap-5">
            {MINI_CAMPS.map((m, i) => (
              <Reveal key={m.id} delay={i * 0.06}>
                <div data-testid={`minicamp-card-${i}`} className="card-lift bg-court-surface/80 border border-white/10 rounded-2xl p-6 h-full flex flex-col">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-mono text-xs uppercase tracking-widest text-court-amber flex items-center gap-2"><CalendarDays size={13} />{m.dates}</span>
                    <span className="font-display font-black text-2xl text-court-amber">$175</span>
                  </div>
                  <h3 className="font-display font-bold uppercase tracking-tight text-xl sm:text-2xl mt-3">{m.title} Mini-Camp</h3>
                  <p className="text-sm text-zinc-400 mt-2 leading-relaxed flex-1">{m.desc}</p>
                  <button data-testid={`minicamp-${i}-select`} onClick={() => { setForm((f) => ({ ...f, week: m.id })); document.getElementById("camp-form")?.scrollIntoView({ behavior: "smooth" }); }}
                    className="mt-5 text-left font-mono text-xs uppercase tracking-widest text-court-amber hover:text-court-amberhover transition-colors">
                    Reserve this mini-camp →
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="mt-20 grid lg:grid-cols-2 gap-12">
          <Reveal>
            <SectionTag index="SEC.02" label="A Day At Camp" />
            <h2 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-2xl sm:text-3xl lg:text-4xl">Every hour <span className="text-court-amber">has a purpose.</span></h2>
            <div className="mt-8 space-y-0">
              {DAILY.map((d, i) => (
                <div key={d.time} className="flex gap-5 group">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-court-amber shrink-0 mt-1.5" />
                    {i < DAILY.length - 1 && <div className="w-px flex-1 bg-white/10" />}
                  </div>
                  <div className="pb-7">
                    <div className="font-mono text-xs uppercase tracking-widest text-court-amber">{d.time}</div>
                    <div className="text-sm text-zinc-200 mt-1">{d.item}</div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div id="camp-form">
              <SectionTag index="SEC.03" label="Reserve A Spot" />
              <h2 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-2xl sm:text-3xl lg:text-4xl">Camp <span className="text-court-amber">registration.</span></h2>
              <p className="mt-3 text-sm text-zinc-400">Reserve now, pay in person on day one — cash, Zelle, or Venmo. Spots are capped per week.</p>
              {sent && (
                <div data-testid="camp-success" className="mt-6 flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/40 rounded-xl px-5 py-4">
                  <CheckCircle2 size={18} className="text-emerald-400" />
                  <p className="text-sm text-emerald-300">Spot reserved. Theo will confirm by email.</p>
                </div>
              )}
              <form onSubmit={submit} className="mt-6 bg-court-surface/90 border border-white/10 rounded-2xl p-7 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input data-testid="camp-parent-input" className="input-dark" placeholder="Parent / guardian name" value={form.parent_name} onChange={set("parent_name")} required />
                  <input data-testid="camp-athlete-input" className="input-dark" placeholder="Athlete name" value={form.athlete_name} onChange={set("athlete_name")} required />
                  <input data-testid="camp-email-input" type="email" className="input-dark" placeholder="Email address" value={form.email} onChange={set("email")} required />
                  <input data-testid="camp-phone-input" className="input-dark" placeholder="Phone number" value={form.phone} onChange={set("phone")} required />
                  <select data-testid="camp-grade-select" className="input-dark" value={form.athlete_grade} onChange={set("athlete_grade")}>
                    {["Middle School", "JV", "Varsity", "College / D1"].map((g) => <option key={g}>{g}</option>)}
                  </select>
                  <select data-testid="camp-week-select" className="input-dark" value={form.week} onChange={set("week")}>
                    {WEEKS.map((w) => <option key={w.id} value={w.id}>{w.id}</option>)}
                    {MINI_CAMPS.map((m) => <option key={m.id} value={m.id}>{m.id}</option>)}
                  </select>
                </div>
                <textarea data-testid="camp-notes-input" className="input-dark min-h-[90px]" placeholder="Anything Theo should know? (experience level, goals, schedule constraints)" value={form.notes} onChange={set("notes")} />
                <button data-testid="camp-register-btn" type="submit" disabled={busy}
                  className="btn-amber w-full bg-court-amber text-court-void font-display font-extrabold uppercase tracking-wide text-lg py-3.5 rounded-lg disabled:opacity-60">
                  {busy ? "Reserving..." : "Reserve Camp Spot"}
                </button>
              </form>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
